
import { fal } from "@fal-ai/client";

// Configure fal client with the API key from environment variables
// Note: In a production app, you should proxy these requests through your backend
// to avoid exposing your API key in the client.
const FAL_KEY = (import.meta as any).env.VITE_FAL_KEY;

fal.config({
    credentials: FAL_KEY,
});

export async function generateVirtualTryOnImageFal(modelImage: string | File, garmentFile: File): Promise<string> {
    const garmentUrl = await fal.storage.upload(garmentFile);

    let modelUrl: string;
    if (modelImage instanceof File) {
        modelUrl = await fal.storage.upload(modelImage);
    } else {
        modelUrl = modelImage;
    }

    const result: any = await fal.subscribe("fal-ai/bytedance/seedream/v4.5/edit", {
        input: {
            prompt: "Apply the clothing item from Figure 2 onto the person in Figure 1. Figure 1 is the person and Figure 2 is the clothing. Ensure the person wears the new clothing naturally. Keep Figure 1 pose and background.",
            image_urls: [modelUrl, garmentUrl],
            image_size: "auto_2K",
        },
        logs: true,
    });

    const images = result.images || (result.data && result.data.images);

    if (images && images.length > 0) {
        return images[0].url;
    }

    throw new Error("Failed to generate image from fal.ai. Check console for details.");
}

export async function generatePoseVariationFal(baseImageUrl: string, poseDescription: string): Promise<string> {
    const result: any = await fal.subscribe("fal-ai/bytedance/seedream/v4.5/edit", {
        input: {
            prompt: `Modify the person's pose in Figure 1 to match this description: ${poseDescription}. Keep the person, clothing, and background the same.`,
            image_urls: [baseImageUrl],
            image_size: "auto_2K",
        },
        logs: true,
    });

    const images = result.images || (result.data && result.data.images);

    if (images && images.length > 0) {
        return images[0].url;
    }

    throw new Error("Failed to change pose with fal.ai");
}

export async function generateModelImageFal(userImage: File): Promise<string> {
    const imageUrl = await fal.storage.upload(userImage);

    const result: any = await fal.subscribe("fal-ai/bytedance/seedream/v4.5/edit", {
        input: {
            prompt: "In Figure 1, transform this person into a professional fashion model. Place them in a clean studio background, standing pose, neutral expression, high definition fashion photography.",
            image_urls: [imageUrl],
            image_size: "auto_2K",
        },
        logs: true,
    });

    const images = result.images || (result.data && result.data.images);

    if (images && images.length > 0) {
        return images[0].url;
    }

    throw new Error("Failed to generate model image from fal.ai. Check console.");
}

export async function generateFabricPatternProcess(fabricFile: File, patternFile: File, finalDesignFile: File): Promise<string> {
    const [fabricUrl, patternUrl, finalDesignUrl] = await Promise.all([
        fal.storage.upload(fabricFile),
        fal.storage.upload(patternFile),
        fal.storage.upload(finalDesignFile)
    ]);

    const result: any = await fal.subscribe("fal-ai/bytedance/seedream/v4.5/edit", {
        input: {
            prompt: "Using the fabric from Figure 1 and the pattern from Figure 2, recreate the final design shown in Figure 3. Apply the pattern onto the fabric as shown in the design. Ensure the texture of the fabric and the details of the pattern are preserved in the final output.",
            image_urls: [fabricUrl, patternUrl, finalDesignUrl],
            image_size: "auto_2K",
        },
        logs: true,
    });

    const images = result.images || (result.data && result.data.images);

    if (images && images.length > 0) {
        return images[0].url;
    }

    throw new Error("Failed to process fabric and pattern with fal.ai");
}
